<template>
	<div
		class="payment-offer-card"
		:class="{
			'is-selected': isSelected,
			'has-multiple': hasMultiple,
			'-t-light': lightTheme,
		}"
		@click="handleSelect"
	>
		<div
			v-if="imgUrl"
			class="payment-offer-card__content-image"
		>
			<img
				class="payment-offer-card__image-img"
				:src="$Helpers.getScaledImageUrl(imgUrl, 100)"
			>
		</div>
		<div class="payment-offer-card__content-info">
			<div class="payment-modal__offer-title">
				{{ title }}
			</div>
			<div
				v-if="description"
				class="payment-modal__offer-desc"
			>
				{{ description }}
			</div>
			<div
				v-if="info"
				class="payment-modal__offer-desc"
			>
				{{ info }}
			</div>
		</div>
		<div class="payment-offer-card__content-price">
			<div class="payment-offer-card__price">
				<template v-if="hasRebate && remainingFee !== null">
					<div class="payment-offer-card__price-rebate-left">
						<div class="payment-offer-card__price-original">
							{{ price }}
						</div>
						<div
							v-if="rebatePct !== null"
							class="payment-offer-card__price-rebate-pct"
						>
							{{ rebateLabel }}
						</div>
					</div>
					<div class="payment-offer-card__price-rebate-right">
						<div class="payment-offer-card__price-rebated">
							{{ formattedRemainingFee }}
						</div>
						<div class="payment-offer-card__currency">
							{{ currencyCode }} {{ validityInfo ? ` / ${validityInfo}` : '' }}
						</div>
					</div>
				</template>
				<template v-else>
					{{ price }}
				</template>
			</div>
			<div
				v-if="!(hasRebate && remainingFee !== null)"
				class="payment-offer-card__currency"
			>
				{{ currencyCode }} {{ validityInfo ? ` / ${validityInfo}` : '' }}
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name:  'PaymentOfferCard',
	props: {
		title: {
			type:     String,
			required: true,
		},
		description: {
			type:    String,
			default: null,
		},
		info: {
			type:    String,
			default: null,
		},
		imgUrl: {
			type:    String,
			default: null,
		},
		price: {
			type:    String,
			default: null,
		},
		currencyCode: {
			type:    String,
			default: null,
		},
		validityInfo: {
			type:    String,
			default: null,
		},
		isSelected: {
			type:    Boolean,
			default: false,
		},
		hasMultiple: {
			type:    Boolean,
			default: false,
		},
		lightTheme: {
			type:    Boolean,
			default: false,
		},
		remainingFee: {
			// Remaining fee in cents (unformatted). Formatting stays in this component to keep it consistent with `price`.
			type:    Number,
			default: null,
		},
		hasRebate: {
			type:    Boolean,
			default: false,
		},
		rebatePct: {
			type:    Number,
			default: null,
		},
	},
	computed: {
		formattedRemainingFee() {
			if (this.remainingFee === null) {
				return null;
			}
			return (this.remainingFee / 100).toFixed(2);
		},
		rebateLabel() {
			if (this.rebatePct === null) {
				return null;
			}

			// `rebatePct` can be negative (e.g. -50). The UI should show an absolute "50% Discount" label.
			// Keep the number formatting consistent and let translations control word order.
			return this.$I18n.trans('payments.discount_label', { pct: Math.abs(this.rebatePct) });
		},
	},
	methods: {
		handleSelect() {
			this.$emit('select');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.payment-offer-card {
	--color-payment-offer-card-primary: var(--color-soft-white);
	--color-payment-offer-card-primary-selected: var(--color-shadow-grey);
	--color-payment-offer-card-secondary: var(--color-grey-300);
	--color-payment-offer-card-secondary-selected: var(--color-grey-800);
	--color-payment-offer-card-background: var(--color-grey-900);
	--color-payment-offer-card-background-selected: var(--color-grey-400);
	--color-payment-offer-card-border-hover: var(--color-grey-700);

	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: $sp4;
	padding: $sp5 $sp4-5;
	background-color: var(--color-payment-offer-card-background);
	border-radius: $border-radius--lg;
	border: 2px solid transparent;
	transition: background-color $trans-time-fast, border-color $trans-time-fast;

	&.-t-light {
		--color-payment-offer-card-primary: var(--color-grey-200);
		--color-payment-offer-card-primary-selected: var(--color-shadow-grey);
		--color-payment-offer-card-secondary: var(--color-grey-400);
		--color-payment-offer-card-secondary-selected: var(--color-grey-200);
		--color-payment-offer-card-background: var(--color-grey-950);
		--color-payment-offer-card-background-selected: var(--color-grey-800);
		--color-payment-offer-card-border-hover: var(--color-grey-800);
	}

	&.is-selected {
		background-color: var(--color-payment-offer-card-background-selected);
	}

	&.has-multiple {
		cursor: pointer;

		&:hover:not(.is-selected) {
			border-color: var(--color-payment-offer-card-border-hover);
		}
	}

	@include size(sm-up) {
		align-items: center;
	}
}

.payment-offer-card__content-image {
	aspect-ratio: 1/1;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 48px;
	width: 48px;
}

.payment-offer-card__image-img {
	height: 48px;
	object-fit: contain;
}

.payment-offer-card__content-info {
	flex-grow: 1;
}

.payment-modal__offer-title {
	@include font(primary, medium, normal, fs-100);
	margin-bottom: $sp1;
	color: var(--color-payment-offer-card-primary);
	transition: color $trans-time-fast;

	.payment-offer-card.is-selected & {
		color: var(--color-payment-offer-card-primary-selected);
	}
}

.payment-modal__offer-desc {
	@include font-size(fs-80);
	margin-bottom: $sp1;
	color: var(--color-payment-offer-card-secondary);
	transition: color $trans-time-fast;

	.payment-offer-card.is-selected & {
		color: var(--color-payment-offer-card-secondary-selected);
	}
}

.payment-offer-card__content-price {
	text-align: right;
}

.payment-offer-card__price {
	@include font(primary, medium, normal, fs-150);
	display: flex;
	align-items: flex-start;
	justify-content: flex-end;
	line-height: 1;
	color: var(--color-payment-offer-card-primary);
	transition: color $trans-time-fast;

	.payment-offer-card.is-selected & {
		color: var(--color-payment-offer-card-primary-selected);
	}
}

.payment-offer-card__price-original {
	@include font-size(fs-150);
	text-decoration: line-through;
	opacity: 0.8;
}

.payment-offer-card__price-rebate-left {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
}

.payment-offer-card__price-rebate-pct {
	@include font-size(fs-60);
}

.payment-offer-card__price-rebate-right {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	margin-left: 10px;
}

.payment-offer-card__price-rebated {
	@include font-size(fs-150);
}

.payment-offer-card__currency {
	@include font-size(fs-60);
	color: var(--color-payment-offer-card-secondary);
	transition: color $trans-time-fast;
	white-space: nowrap;

	.payment-offer-card.is-selected & {
		color: var(--color-payment-offer-card-secondary-selected);
	}
}

</style>
